import { MAX_ACTIVE_IMAGES_PER_PERSON } from '@sentra/contracts';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiMutation } from '@/api/mutations';
import { usePersonImages } from '@/api/queries';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { hasRoom } from '@/features/persons/images/accept';
import { type MediaDevicesLike, platformMedia } from '@/features/persons/images/camera';
import type { CaptureCanvas } from '@/features/persons/images/capture';
import { ImageGallery } from '@/features/persons/images/gallery';
import { ImageUpload } from '@/features/persons/images/upload';
import { WebcamCapture } from '@/features/persons/images/webcam';

/**
 * A record's photographs, end to end: what it has, adding one, and taking one away.
 *
 * **Two routes add an image and they are not interchangeable.** An upload is a file part and a
 * capture is base64 in a JSON body, and the server records `capturedVia` from which one was used —
 * a fact an operator can see with their own eyes and which the print layout and the audit trail both
 * distinguish. So the two controls call two routes rather than one route with a flag.
 *
 * **The five-image cap is surfaced before the controls, not after an upload.** It is enforced in
 * three layers on the server, so the sixth is refused whatever this does; what changes is whether
 * the operator finds out before or after they chose the photograph.
 *
 * **The camera platform and the canvas are injected**, because neither exists in `jsdom`: without
 * that the three cases worth covering — permission granted, permission denied, the device
 * disappearing — could not be tested at all, and the mirror decision could only be checked by
 * looking at a screen, which is exactly where it is invisible.
 */
export function PersonImagesPanel({
  personId,
  media = platformMedia(),
  storage = globalThis.localStorage,
  createCanvas,
}: Readonly<{
  personId: string;
  media?: MediaDevicesLike | null;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  createCanvas?: () => CaptureCanvas;
}>): ReactNode {
  const { t } = useTranslation();
  const describe = useApiErrorMessage();
  const { images } = usePersonImages(personId);

  const active = images.filter((image) => image.status === 'active').length;
  const full = !hasRoom(active);

  // Which photograph a removal is in flight for. One shared flag disabled every row's control while
  // any one removal was running, which reads as the whole gallery being locked.
  const [removingId, setRemovingId] = useState<string | null>(null);

  const upload = useApiMutation<typeof apiRoutes.personImageUpload, File>(
    apiRoutes.personImageUpload,
    (file) => {
      const form = new FormData();
      // `file` is the one part name the server's multipart reader accepts; anything else is refused
      // rather than ignored.
      form.append('file', file, file.name);
      return { params: { id: personId }, form };
    },
  );

  const capture = useApiMutation<typeof apiRoutes.personImageCapture, string>(
    apiRoutes.personImageCapture,
    (frame) => ({ params: { id: personId }, body: { frame } }),
  );

  const remove = useApiMutation<typeof apiRoutes.personImageRemove, string>(
    apiRoutes.personImageRemove,
    (imageId) => ({ params: { id: personId, imageId } }),
    {
      onSuccess: () => {
        setRemovingId(null);
      },
    },
  );

  const failure: unknown = upload.error ?? capture.error ?? remove.error;
  // Spread rather than passed as `undefined`, because the prop is optional and `exactOptionalPropertyTypes`
  // distinguishes "absent" from "present and undefined".
  const canvasProp = createCanvas === undefined ? {} : { createCanvas };

  return (
    <section className="flex flex-col gap-4" data-testid="images-panel">
      <h3 className="text-lg font-semibold tracking-tight">{t('person.images.title')}</h3>

      <ImageGallery
        personId={personId}
        images={images}
        removing={remove.submitting ? removingId : null}
        onRemove={(imageId) => {
          setRemovingId(imageId);
          remove.submit(imageId);
        }}
      />

      {failure !== null && failure !== undefined && (
        <Alert variant="destructive" role="alert">
          <AlertTitle>{t('person.images.failedTitle')}</AlertTitle>
          <AlertDescription>{describe(failure)}</AlertDescription>
        </Alert>
      )}

      {full && (
        <Alert role="status" data-testid="images-full">
          <AlertDescription>
            {t('person.images.capReached', { limit: MAX_ACTIVE_IMAGES_PER_PERSON })}
          </AlertDescription>
        </Alert>
      )}
      {!full && (
        <div className="flex flex-col gap-6">
          <ImageUpload
            activeImages={active}
            uploading={upload.submitting}
            onChoose={(file) => {
              upload.submit(file);
            }}
          />
          <WebcamCapture
            media={media}
            storage={storage}
            {...canvasProp}
            busy={capture.submitting}
            onCapture={(frame) => {
              capture.submit(frame);
            }}
          />
        </div>
      )}
    </section>
  );
}
